export const BASE_URL = process.env.REACT_APP_API_URL || 'https://mannaplag.mannaanalytics.com/api';

export const TokenStorage = {
  getAccess: () => localStorage.getItem('access_token'),
  getRefresh: () => localStorage.getItem('refresh_token'),
  setTokens: (access: string, refresh: string) => {
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
  },
  clear: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  },
  setUser: (user: object) => localStorage.setItem('user', JSON.stringify(user)),
  getUser: () => {
    const u = localStorage.getItem('user');
    return u ? JSON.parse(u) : null;
  },
};

// ── Token refresh lock — prevents multiple concurrent refresh calls ──
let isRefreshing = false;
let refreshQueue: Array<(token: string) => void> = [];

async function attemptTokenRefresh(): Promise<string | null> {
  const refresh = TokenStorage.getRefresh();
  if (!refresh) return null;

  if (isRefreshing) {
    // Another request is already refreshing — queue this one
    return new Promise((resolve) => {
      refreshQueue.push((newToken: string) => resolve(newToken));
    });
  }

  isRefreshing = true;
  try {
    const res = await fetch(`${BASE_URL}/auth/token/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
    });

    if (!res.ok) {
      // Refresh token itself is expired — clear everything
      TokenStorage.clear();
      const path = window.location.pathname;
      // Don't force-navigate away from the admin panel, or if we're already
      // on /login — doing so was interrupting people mid-login on the other
      // panel in the same browser tab/session.
      if (!path.startsWith('/superadmin') && path !== '/login') {
        window.location.href = '/login';
      }
      return null;
    }

    const data = await res.json();
    const newAccess = data.access;
    const newRefresh = data.refresh || refresh;
    TokenStorage.setTokens(newAccess, newRefresh);

    // Resolve all queued requests with new token
    refreshQueue.forEach(cb => cb(newAccess));
    refreshQueue = [];
    return newAccess;
  } catch {
    TokenStorage.clear();
    const path = window.location.pathname;
    if (!path.startsWith('/superadmin') && path !== '/login') {
      window.location.href = '/login';
    }
    return null;
  } finally {
    isRefreshing = false;
  }
}

// ── Main fetch wrapper with auto-refresh ────────────────────
export async function apiFetch(
  endpoint: string,
  options: RequestInit = {},
  retry = true,
): Promise<any> {
  const token = TokenStorage.getAccess();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (!headers['Content-Type'] && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(`${BASE_URL}${endpoint}`, { ...options, headers });

  // Auto-refresh on 401
  if (res.status === 401 && retry) {
    const newToken = await attemptTokenRefresh();
    if (newToken) {
      return apiFetch(endpoint, options, false); // retry once with new token
    }
    return null;
  }

  if (!res.ok) {
    let data: any = {};
    try { data = await res.json(); } catch { }
    const values = Object.values(data) as any[];
    const message: string =
      data?.error ||
      data?.detail ||
      (Array.isArray(values[0]) ? values[0][0] : String(values[0])) ||
      'Something went wrong.';
    throw new Error(message);
  }

  // 204 No Content
  if (res.status === 204) return null;

  return res.json();
}

// ── Legacy request function (kept for backward compat) ───────
async function request(
  endpoint: string,
  method: string = 'GET',
  body?: object,
  auth: boolean = false,
) {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (auth) {
    const token = TokenStorage.getAccess();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(`${BASE_URL}${endpoint}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) {
    const values = Object.values(data) as any[];
    const message: string =
      data?.error ||
      data?.detail ||
      (Array.isArray(values[0]) ? values[0][0] : String(values[0])) ||
      'Something went wrong.';
    throw new Error(message);
  }
  return data;
}

export const AuthAPI = {
  register: (payload: {
    email: string;
    password: string;
    confirm_password: string;
    full_name: string;
    phone: string;
    role: string;
    organisation?: string;
  }) => request('/auth/register/', 'POST', payload),

  verifyOTP: (email: string, code: string) =>
    request('/auth/verify-otp/', 'POST', { email, code }),

  resendOTP: (email: string) =>
    request('/auth/resend-otp/', 'POST', { email }),

  // Same endpoint handles normal email/password login AND Google login.
  // Backend decides which flow to run based on `google_login`.
  login: (payload: {
    email: string;
    password?: string;
    google_login?: number;   // 0 = normal login, 1 = google login
    id_token?: string;      // Google's `sub` claim
    full_name?: string;      // Google's `name` claim (used if account is new)
  }) =>
    request('/auth/login/', 'POST', {
      password: '',
      google_login: 0,
      id_token: '',
      full_name: '',
      ...payload,
    }),

  logout: (refresh: string) =>
    request('/auth/logout/', 'POST', { refresh }, true),

  refreshToken: (refresh: string) =>
    request('/auth/token/refresh/', 'POST', { refresh }),

  me: () => apiFetch('/auth/me/'),
  updateProfile: (payload: {
    full_name?: string;
    phone_number?: string;
  }) => apiFetch('/accounts/me/update/', { method: 'POST', body: JSON.stringify(payload) }),
  changePassword: (payload: {
    current_password: string;
    new_password: string;
  }) => apiFetch('/auth/change-password/', { method: 'POST', body: JSON.stringify(payload) }),
};

export interface Ticket {
  ticket_id: number;
  ticket_number: string;
  name: string;
  email: string;
  subject: string;
  admin_reply:string;
  message: string;
  category: string;
  priority: 'Low' | 'Medium' | 'High' | string;
  status: string;
  source?: string;
  created_at: string;
  updated_at: string;
}

export const TicketsAPI = {
  // POST /tickets/create/
  create: (payload: {
    name: string;
    email: string;
    subject: string;
    message: string;
    category: string;
    priority: string;
    source?: string;
  }) =>
    apiFetch('/tickets/create/', {
      method: 'POST',
      body: JSON.stringify({ source: 'Website', ...payload }),
    }),

  // GET /tickets/my/
  my: (): Promise<Ticket[]> => apiFetch('/tickets/my/'),

  // GET /tickets/{ticket_id}/
  detail: (ticketId: number | string): Promise<Ticket> => apiFetch(`/tickets/${ticketId}/`),

  // POST /tickets/{ticket_id}/reopen/
  reopen: (ticketId: number | string) =>
    apiFetch(`/tickets/${ticketId}/reopen/`, { method: 'POST' }),
};

export const PlansAPI = {
  list: (type?: string, billingCycle?: string, currency?: string) => {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (billingCycle) params.append('billing_cycle', billingCycle);
    if (currency) params.append('currency', currency);
    return request(`/plans/?${params.toString()}`);
  },
  get: (planId: string) => request(`/plans/${planId}/`),
  detectCurrency: () => request('/plans/detect-currency/'),
};